import * as R from '../../Router/routes';
import {
  AsyncCardPayment,
  AsyncExtensions,
  AsyncRepaymentWireTransfer,
} from '../../Router/Router';
import { useTranslation } from 'react-i18next';
import BizumIcon from 'assets/icons/bizum.svg?react';
import useClientDebtRestructuring from '../../hooks/webapi/useClientDebtRestructuring';
import useError from '../../hooks/useError';

import {
  Card,
  Heading,
  MenuButton,
  PenaltyNotification,
  Placeholder,
} from '../../components';
import { Helmet } from 'react-helmet-async';
import { memo, useEffect, useMemo, useState } from 'react';
import { numberToCurrency } from 'utils/translate';
import {
  QUERY_BACK_ROUTE,
  QUERY_BIZUM,
  QUERY_FULL_REPAYMEMT,
} from 'utils/constants';
import { useCardPayments, useLatestLoan } from '../../hooks/webapi';
import { useNavigate } from '@reach/router';
import BankCardIcon from 'assets/icons/bank-card-circle.svg?react';
import BankIcon from 'assets/icons/bank-circle.svg?react';
import CalendarIcon from 'assets/icons/calendar-circle.svg?react';
import Dashboard from '../Layouts/Dashboard/Dashboard';
import ErrorIcon from 'assets/icons/error-info.svg?react';
import styles from './FullRepaymentDefault.module.scss';

function preloadRepaymentWireTransfer() {
  AsyncRepaymentWireTransfer.preload();
}

function preloadExtensions() {
  AsyncExtensions.preload();
}

function preloadCardPayment() {
  AsyncCardPayment.preload();
}

export default memo(function FullRepaymentDefault() {
  const { t } = useTranslation('repayment');
  const { showError } = useError();
  const { latestLoan } = useLatestLoan();
  const [loading, setLoading] = useState(false);
  const [navigationDisabled, setNavigationDisabled] = useState(false);
  const navigate = useNavigate();
  const { getBizumPayments } = useCardPayments();
  const { isDebtRestructuring } = useClientDebtRestructuring();

  const urlParams = new URLSearchParams(
    decodeURIComponent(window.location.search),
  );
  const backRoute = urlParams.get('backRoute');

  useEffect(() => {
    preloadRepaymentWireTransfer();
    preloadExtensions();
    preloadCardPayment();
  }, []);

  const onBizumOptionClicked = async () => {
    const successURLPayment =
      latestLoan?.productCode === 'CONS' || isDebtRestructuring
        ? import.meta.env.VITE_ROOT_URL + R.CARD_PAYMENT_CONS_SUCCESS
        : import.meta.env.VITE_ROOT_URL + R.CARD_PAYMENT_MINI_SUCCESS;

    const failureURLPayment =
      latestLoan?.productCode === 'CONS' || isDebtRestructuring
        ? `${
            import.meta.env.VITE_ROOT_URL + R.CARD_PAYMENT_CONS_FAILURE
          }?${QUERY_FULL_REPAYMEMT}&${QUERY_BACK_ROUTE}=${
            R.FULL_REPAYMENT
          }&${QUERY_BIZUM}`
        : `${
            import.meta.env.VITE_ROOT_URL + R.CARD_PAYMENT_FAILURE
          }?${QUERY_FULL_REPAYMEMT}&${QUERY_BACK_ROUTE}=${
            R.FULL_REPAYMENT
          }&${QUERY_BIZUM}`;

    if (!latestLoan?.payNowAmount) {
      showError();
    } else {
      setNavigationDisabled(true);
      setLoading(true);
      const response = await getBizumPayments({
        body: {
          amount: latestLoan?.payNowAmount,
          successUrl: successURLPayment,
          failureUrl: failureURLPayment,
        },
      });

      setLoading(false);
      setNavigationDisabled(false);

      if (response?.payFormUrl) {
        navigate(response?.payFormUrl);
      } else if (response?.status === 'ERROR') {
        if (response?.errorCode === 'DUPLICATE_PAYMENT' && false) {
          showError({ message: t('error:duplicatePayment') });
        } else showError();
      }
    }
  };

  /** The extension with the highest term */
  const maximumExtension = useMemo(
    () =>
      latestLoan?.loanExtensionFees.reduce(
        (acc: any, item) => (acc = acc.term > item.term ? acc : item),
        0,
      ),
    [latestLoan?.loanExtensionFees],
  );

  return (
    <Dashboard
      backUrl={backRoute || R.ACTIVE_LOAN}
      backUrlDisabled={navigationDisabled}
      menuDisabled={navigationDisabled}
      small
    >
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <Heading
        className={styles.heading}
        size="l"
        dataTest="fullRepayment-heading"
      >
        <Placeholder loading={loading} dataTest="fullRepayment-heading-text">
          {t('heading_full_repayment')}
        </Placeholder>
      </Heading>

      {latestLoan &&
        (latestLoan.penaltyAmount > 0 || latestLoan.commissionAmount > 0) && (
          <PenaltyNotification
            loading={loading}
            penalty={latestLoan.penaltyAmount}
            overdue={latestLoan.overDueDays}
            commission={latestLoan.commissionAmount}
          />
        )}

      <Card
        className={styles['offer-card']}
        dataTest="fullRepayment-offer-card"
      >
        <div>
          <div className={styles.row}>
            <Placeholder
              loading={loading}
              className={styles.bold}
              dataTest="fullRepayment-offer-repaymentTotal-text"
            >
              {t('early_repayment_total')}
            </Placeholder>
            <Placeholder
              loading={loading}
              dataTest="fullRepayment-offer-repaymentTotal-value"
            >
              {numberToCurrency(latestLoan?.payNowAmount)}
            </Placeholder>
          </div>

          <div className={styles.row}>
            <Placeholder
              loading={loading}
              dataTest="fullRepayment-earlyRepaymentPrincipal-text"
            >
              {t('early_repayment_principal')}
            </Placeholder>
            <Placeholder
              loading={loading}
              dataTest="fullRepayment-earlyRepaymentPrincipal-value"
            >
              {numberToCurrency(latestLoan?.openAmount)}
            </Placeholder>
          </div>

          {!!latestLoan?.earlyFullRepaymentFee && (
            <div className={styles.row}>
              <Placeholder
                loading={loading}
                dataTest="fullRepayment-earlyRepaymentFee-text"
              >
                {t('early_repayment_fee')}
              </Placeholder>
              <Placeholder
                loading={loading}
                dataTest="fullRepayment-earlyRepaymentFee-value"
              >
                {numberToCurrency(latestLoan?.earlyFullRepaymentFee)}
              </Placeholder>
            </div>
          )}
        </div>

        {!!latestLoan?.earlyFullRepaymentFee && (
          <div
            className={styles['fee-notification']}
            data-test="fullRepayment-feeNotif"
          >
            <div className={styles['fee-icon']}>
              <ErrorIcon data-test="fullRepayment-feeNotif-icon" />
            </div>
            <div
              data-test="fullRepayment-feeNotif-text"
              dangerouslySetInnerHTML={{
                __html: t('early_repayment_text'),
              }}
            ></div>
          </div>
        )}
      </Card>

      <Card className={styles.menu}>
        <MenuButton
          loading={loading}
          dataTest="card-payment-button"
          className={styles['menu-button']}
          heading={t('card_heading')}
          info={t('card_info')}
          Icon={BankCardIcon}
          url={`${R.CARD_PAYMENT}?${QUERY_FULL_REPAYMEMT}&${QUERY_BACK_ROUTE}=${R.FULL_REPAYMENT}`}
          isPopular
          analyticsIsTrackable={true}
          analyticsTrackingDescription="Full repayment card menu button"
        />
        <MenuButton
          analyticsIsTrackable={true}
          analyticsTrackingDescription="Bizum full repayment button"
          loading={loading}
          dataTest="bizum-full-repayment-button"
          className={styles['menu-button']}
          heading={t('bizum_heading')}
          info={t('repayment_extension_payment:bizum_info')}
          Icon={BizumIcon}
          onClick={onBizumOptionClicked}
        />
        <MenuButton
          loading={loading}
          dataTest="wire-transfer-button"
          className={styles['menu-button']}
          heading={t('manual_transfer_heading')}
          info={t('manual_transfer_info')}
          Icon={BankIcon}
          url={`${R.REPAYMENT_WIRE_TRANSFER}?fullRepayment=true`}
          analyticsIsTrackable={true}
          analyticsTrackingDescription="Full repayment wire transfer menu button"
        />
      </Card>

      {maximumExtension ? (
        <Card className={styles.menu}>
          <MenuButton
            loading={loading}
            dataTest="extensions-button"
            className={styles['menu-button']}
            heading={t('postpone_heading')}
            info={t('postpone_info')}
            Icon={CalendarIcon}
            url={`${R.EXTENSIONS}?PID=${latestLoan?.productNumber}`}
            analyticsIsTrackable={true}
            analyticsTrackingDescription="Extension button"
          />
        </Card>
      ) : null}
    </Dashboard>
  );
});
